/**
 * API client for V5 UI.
 *
 * Calls the Laravel V5 API at /api/v5.
 * Uses the same JWT Bearer token from the Zustand auth store.
 */

import { useAuthStore } from "@/store/authStore";
import type { StaffUser } from "@/types/auth.types";
import type { ApiError } from "@/types/api.types";

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? "/api/v5";

/**
 * A non-OK API response, thrown with the server's own error code attached.
 *
 * `message` keeps the exact `[status] detail` string this client has always
 * thrown, because callers across the V5 UI read `err.message` directly and a
 * reworded string would change what they display. The extra fields are additive:
 * they let a caller tell a 502 `STS_ERROR` apart from a 429 `RATE_LIMITED`
 * instead of pattern-matching prose. `api.test.ts` pins both halves.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(
    status: number,
    message: string,
    extra: { code?: string; details?: unknown } = {}
  ) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = extra.code;
    this.details = extra.details;
  }
}

interface PersistedAuthState {
  token?: string | null;
  accountId?: number | null;
  user?: Partial<StaffUser> | null;
}

interface LaravelEnvelope<T> {
  data: T;
  meta?: unknown;
}

function readPersistedAuthState(): PersistedAuthState {
  try {
    const store = JSON.parse(localStorage.getItem("ftp-auth") || "{}");
    return (store?.state ?? {}) as PersistedAuthState;
  } catch {
    return {};
  }
}

function getToken(): string | null {
  return useAuthStore.getState().token ?? readPersistedAuthState().token ?? null;
}

function getAccountId(): string | null {
  const accountId = useAuthStore.getState().accountId ?? readPersistedAuthState().accountId;
  return accountId ? String(accountId) : null;
}

/**
 * Auth headers for callers that must use raw `fetch` (multipart uploads, blob
 * downloads) instead of `api()`.
 *
 * Resolves the token exactly as `api()` does — this tab's store first, the
 * persisted copy only as a fallback. Reading localStorage alone is wrong: it is
 * shared by every tab and nothing re-syncs this tab's store from it, so after a
 * logout or re-login in another tab it can hold no token (or a different one)
 * while this tab is still signed in. A note then saved through `api()` and its
 * attachment POST, sent from localStorage, came back 401.
 */
export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getToken();
  const accountId = getAccountId();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (accountId) headers["X-Target-Company"] = accountId;
  return headers;
}

export interface AuthUser {
  userID: number;
  accountID: number;
  firstName: string;
  lastName: string;
  email: string;
  userLevel: string;
  /** Raw users.role string; null when the user has no role assigned. */
  role?: string | null;
  /**
   * Account-customized title for the user's role, resolved from
   * aa_accounts_roles on the server. Null when no role is set.
   */
  roleTitle?: string | null;
  avatarUrl?: string | null;
}

function mapAuthUserToStaffUser(user: AuthUser): StaffUser {
  const displayName = `${user.firstName} ${user.lastName}`.trim();

  return {
    id: user.userID,
    accountId: user.accountID,
    username: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    userLevel: Number(user.userLevel) as StaffUser["userLevel"],
    role: user.role ?? null,
    roleTitle: user.roleTitle ?? null,
    displayName,
    avatarUrl: user.avatarUrl ?? null,
    facilityIds: [],
    isActive: true,
    createdAt: "",
    updatedAt: "",
  };
}

export function setAuth(token: string, user: AuthUser) {
  useAuthStore.getState().login(token, mapAuthUserToStaffUser(user));
  localStorage.removeItem("ftp_token");
  localStorage.removeItem("ftp_user");
}

export function clearAuth() {
  useAuthStore.getState().logout();
  localStorage.removeItem("ftp_token");
  localStorage.removeItem("ftp_user");
}

export function getUser(): AuthUser | null {
  const storeUser = useAuthStore.getState().user;
  if (storeUser) {
    return {
      userID: storeUser.id,
      accountID: storeUser.accountId,
      firstName: storeUser.firstName,
      lastName: storeUser.lastName,
      email: storeUser.email,
      userLevel: String(storeUser.userLevel),
      role: storeUser.role ?? null,
      roleTitle: storeUser.roleTitle ?? null,
      avatarUrl: storeUser.avatarUrl,
    };
  }

  const persistedUser = readPersistedAuthState().user;
  if (persistedUser) {
    return {
      userID: persistedUser.id ?? 0,
      accountID: persistedUser.accountId ?? 0,
      firstName: persistedUser.firstName ?? "",
      lastName: persistedUser.lastName ?? "",
      email: persistedUser.email ?? "",
      userLevel: String(persistedUser.userLevel ?? "1"),
      role: persistedUser.role ?? null,
      roleTitle: persistedUser.roleTitle ?? null,
      avatarUrl: persistedUser.avatarUrl ?? null,
    };
  }

  return null;
}

export function unwrapApiData<T>(payload: T | LaravelEnvelope<T>): T {
  if (
    payload &&
    typeof payload === "object" &&
    "data" in payload
  ) {
    return (payload as LaravelEnvelope<T>).data;
  }

  return payload as T;
}

interface ApiOptions {
  method?: string;
  body?: Record<string, unknown>;
  params?: Record<string, string>;
  noRedirectOn401?: boolean;
}

/**
 * Authenticated GET for diagnostic side-requests whose only consumer is the
 * Network tab. Resolves to nothing and can never affect the page.
 *
 * Deliberately NOT built on api(): that helper hard-navigates to /login on 401
 * and to /unauthorized on 403, which is right for a request the user is waiting
 * on and completely wrong for a background probe — a probe that 403'd would
 * throw the user out of the very screen it was measuring. Nothing here
 * redirects, and nothing here throws.
 *
 * It shares getToken/getAccountId/API_BASE with api() on purpose: a probe that
 * guessed at the base URL or the auth headers would fail on exactly the
 * deployed environments where it is needed.
 */
export function apiProbe(path: string, params?: Record<string, string>): void {
  try {
    const token = getToken();
    const accountId = getAccountId();

    let url = `${API_BASE}${path}`;
    if (params) {
      const qs = new URLSearchParams(params).toString();
      url += (url.includes("?") ? "&" : "?") + qs;
    }

    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (accountId) headers["X-Target-Company"] = accountId;

    // Response intentionally not read: it is inspected in the Network tab, and
    // the server writes its own log line per stage as it goes.
    void fetch(url, { method: "GET", headers }).catch(() => {});
  } catch {
    // A probe must never be the reason anything breaks.
  }
}

export async function api<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const { method = "GET", body, params } = options;
  const token = getToken();
  const accountId = getAccountId();

  let url = `${API_BASE}${path}`;
  if (params) {
    const qs = new URLSearchParams(params).toString();
    url += (url.includes("?") ? "&" : "?") + qs;
  }

  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (accountId) headers["X-Target-Company"] = accountId;

  const init: RequestInit = { method, headers };

  if (body && method !== "GET") {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  const res = await fetch(url, init);

  if (res.status === 401 && !options.noRedirectOn401) {
    clearAuth();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (res.status === 403) {
    window.location.href = "/unauthorized";
    throw new Error("Forbidden");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({} as ApiError));
    const detail =
      (err as ApiError).error?.message ||
      (err as ApiError).message ||
      "API error";
    throw new ApiRequestError(res.status, `[${res.status}] ${detail}`, {
      code: (err as ApiError).error?.code,
      details: (err as ApiError).error?.details,
    });
  }

  return res.json() as Promise<T>;
}
